import { Controller } from '@hotwired/stimulus'

/**
 * Stimulus-контроллер для сортировки списка DOM-элементов по значениям
 * `data-*` атрибутов.
 *
 * Ожидает, что каждая опция сортировки содержит `data-sort` с названием
 * атрибута в kebab-case, например `high-rating`, а каждый item target
 * содержит соответствующий атрибут `data-high-rating`.
 *
 * @extends {Controller}
 */
export default class SortController extends Controller {
  /**
   * Опции сортировки, элементы списка и контейнер списка.
   *
   * @type {string[]}
   */
  static targets = ['option', 'item', 'list']

  /**
   * Невалидные типы сортировки, сгруппированные по значению `data-sort`.
   *
   * @type {Set<string>}
   */
  #invalidSortTypes = new Set()

  /**
   * Проверяет доступные варианты сортировки и применяет активную опцию
   * при инициализации контроллера.
   *
   * @returns {void}
   */
  connect() {
    this.#invalidSortTypes.clear()

    const validOptions = this.optionTargets.filter((option) => {
      const isValid = this.#validateSortType(option.dataset.sort)
      this.#setOptionDisabled(option, !isValid)

      return isValid
    })

    const activeOption = validOptions.find((option) =>
      option.classList.contains('is-active')
    )

    const selectedOption = activeOption || validOptions[0]

    if (selectedOption) {
      this.#setActiveOption(selectedOption)
      this.#updateButtonText(selectedOption)
      this.#sortItems(selectedOption.dataset.sort)
    }
  }

  /**
   * Обрабатывает выбор новой опции сортировки из dropdown.
   *
   * @param {MouseEvent} event - Событие клика по опции сортировки.
   * @returns {void}
   */
  changeSort(event) {
    const target =
      event.currentTarget.closest('[data-sort-target="option"]') ||
      event.currentTarget.closest('[data-sort]')
    const sort = target?.dataset.sort

    if (!target || !this.#validateSortType(sort)) {
      this.#setOptionDisabled(target, true)
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    this.#setOptionDisabled(target, false)
    this.#setActiveOption(target)
    this.#updateButtonText(target)
    this.#sortItems(sort)
  }

  /**
   * Обновляет текст кнопки dropdown выбранной опцией.
   *
   * @param {HTMLElement} option - Выбранная опция сортировки.
   * @returns {void}
   */
  #updateButtonText(option) {
    if (!option || this.#isInvalidSortType(option.dataset.sort)) return

    const button = this.element.querySelector('[data-dropdown-target="btn"]')
    const buttonTextElement = button?.querySelector('span')

    if (buttonTextElement) {
      buttonTextElement.textContent = option.textContent.trim()
    }
  }

  /**
   * Сортирует item targets по выбранному `data-*` атрибуту в порядке
   * убывания и заново вставляет их в контейнер списка.
   *
   * @param {string} sortType - Название `data-*` атрибута в kebab-case.
   * @returns {void}
   */
  #sortItems(sortType) {
    if (!this.#validateSortType(sortType)) return

    const sortKey = this.#sortTypeToDatasetKey(sortType)
    const items = [...this.itemTargets]

    items.sort((a, b) => {
      const aValue = this.#getSortValue(a, sortKey)
      const bValue = this.#getSortValue(b, sortKey)

      return this.#compareSortValues(aValue, bValue)
    })

    this.listTarget.append(...items)
  }

  /**
   * Проверяет, что для выбранного типа сортировки у всех item targets есть
   * соответствующий `data-*` атрибут.
   *
   * @param {string | undefined} sortType - Название `data-*` атрибута в kebab-case.
   * @returns {boolean} `true`, если сортировку можно безопасно применять.
   */
  #validateSortType(sortType) {
    if (!sortType) {
      console.error('SortController: Missing data-sort value.')
      this.#invalidSortTypes.add(this.#invalidSortTypeKey(sortType))
      return false
    }

    const sortKey = this.#sortTypeToDatasetKey(sortType)
    const invalidItem = this.itemTargets.find(
      (item) => this.#getSortValue(item, sortKey) === null
    )

    if (invalidItem) {
      console.error(
        `SortController: Not all items have a data-${sortType} value.`,
        invalidItem
      )
      this.#invalidSortTypes.add(this.#invalidSortTypeKey(sortType))
      return false
    }

    this.#invalidSortTypes.delete(this.#invalidSortTypeKey(sortType))
    return true
  }

  /**
   * Обновляет активную опцию сортировки.
   *
   * @param {HTMLElement} option - Валидная опция сортировки.
   * @returns {void}
   */
  #setActiveOption(option) {
    this.optionTargets.forEach((item) => {
      item.classList.toggle('is-active', item === option)
    })
  }

  /**
   * Отключает только конкретную невалидную опцию.
   *
   * @param {HTMLElement | null | undefined} option - Опция сортировки.
   * @param {boolean} disabled - Нужно ли отключить опцию.
   * @returns {void}
   */
  #setOptionDisabled(option, disabled) {
    if (!option) return

    option.disabled = disabled
  }

  /**
   * Проверяет, помечен ли тип сортировки как невалидный.
   *
   * @param {string | undefined} sortType - Название `data-*` атрибута в kebab-case.
   * @returns {boolean}
   */
  #isInvalidSortType(sortType) {
    return this.#invalidSortTypes.has(this.#invalidSortTypeKey(sortType))
  }

  /**
   * Возвращает стабильный ключ для Set невалидных типов.
   *
   * @param {string | undefined} sortType - Название `data-*` атрибута в kebab-case.
   * @returns {string}
   */
  #invalidSortTypeKey(sortType) {
    return sortType || ''
  }

  /**
   * Преобразует kebab-case имя `data-*` атрибута в ключ `dataset`.
   *
   * @param {string} sortType - Название `data-*` атрибута в kebab-case.
   * @returns {string} Ключ для обращения к `HTMLElement.dataset`.
   */
  #sortTypeToDatasetKey(sortType) {
    return sortType
      .split('-')
      .map((word, index) => {
        if (index === 0) return word

        return word.charAt(0).toUpperCase() + word.slice(1)
      })
      .join('')
  }

  /**
   * Сравнивает два значения сортировки в порядке убывания.
   *
   * @param {string | number | null} aValue - Значение первого элемента.
   * @param {string | number | null} bValue - Значение второго элемента.
   * @returns {number} Порядок элементов для `Array.prototype.sort`.
   */
  #compareSortValues(aValue, bValue) {
    if (aValue === bValue) return 0
    if (aValue === null) return 1
    if (bValue === null) return -1
    if (aValue > bValue) return -1
    if (aValue < bValue) return 1

    return 0
  }

  /**
   * Возвращает значение сортировки из `dataset`.
   *
   * @param {HTMLElement} item - Элемент списка, который сортируется.
   * @param {string} sortKey - Ключ для обращения к `HTMLElement.dataset`.
   * @returns {string | number | null} Значение для сортировки или `null`, если атрибут отсутствует.
   */
  #getSortValue(item, sortKey) {
    const rawValue = item.dataset[sortKey]

    if (typeof rawValue === 'undefined') return null

    const value = rawValue.trim()
    const numericValue = Number(value)

    return value !== '' && Number.isFinite(numericValue) ? numericValue : value
  }
}
